// Small building blocks shared by every screen, each one the React version of
// a helper in docs/reference/ui-prototype.html (av, chead, stepPill, ...).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { ROUTE, type Stage } from '@ouradent/shared';
import { useTeam } from '../lib/data';
import { dayDiff, fDay, fShort, initials } from '../lib/format';
import type { BoardLead, Profile } from '../lib/types';
import { Icon } from './icons';

// Team lookup ------------------------------------------------------------------

const FALLBACK = { full_name: '?', avatar_bg: '#eee', avatar_fg: '#555' };

export function useWho() {
  const { data } = useTeam();
  return useMemo(() => {
    const byId = new Map((data ?? []).map((p) => [p.id, p]));
    return (
      id: string | null | undefined,
    ): Pick<Profile, 'full_name' | 'avatar_bg' | 'avatar_fg'> => (id && byId.get(id)) || FALLBACK;
  }, [data]);
}

// Profile colours are picked by people, so the initials fall back to near-black
// or white when the chosen pair is below WCAG AA contrast.
function luminance(hex: string) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [n >> 16, (n >> 8) & 255, n & 255].map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: number, b: number) {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function readableOn(bg: string, fg: string) {
  const lb = luminance(bg);
  const lf = luminance(fg);
  if (lb === null || lf === null || contrast(lb, lf) >= 4.5) return fg;
  return contrast(lb, 0) >= contrast(lb, 1) ? '#0F1B2D' : '#FFFFFF';
}

export function Avatar({ id, small }: { id: string | null | undefined; small?: boolean }) {
  const who = useWho();
  const p = who(id);
  return (
    <span
      className={`av ${small ? 's' : ''}`}
      style={{
        background: p.avatar_bg ?? '#eee',
        color: readableOn(p.avatar_bg ?? '#eee', p.avatar_fg ?? '#555'),
      }}
      title={p.full_name}
    >
      {initials(p.full_name)}
    </span>
  );
}

export function OwnerOptions({ includeExternal = true }: { includeExternal?: boolean }) {
  const { data } = useTeam();
  return (
    <>
      {(data ?? [])
        .filter((p) => includeExternal || !p.external)
        .map((p) => (
          <option key={p.id} value={p.id}>
            {p.full_name}
          </option>
        ))}
    </>
  );
}

// Card header ------------------------------------------------------------------

export function Chead({
  label,
  cls,
  children,
}: {
  label: string;
  cls: 'g' | 'p' | 't';
  children?: ReactNode;
}) {
  return (
    <div className="chead">
      {cls === 'g' && <span className="dot" />}
      <span className={`lbl ${cls}`}>{label}</span>
      {children}
    </div>
  );
}

export function ViewAll({ to }: { to: string }) {
  return (
    <Link className="viewall" to={to}>
      View all <Icon name="arrow" />
    </Link>
  );
}

// Lead badges ------------------------------------------------------------------

export function StepPill({ lead }: { lead: BoardLead }) {
  switch (lead.step_state) {
    case 'review':
      return <span className="pill sm">Call to review</span>;
    case 'missing':
      return <span className="pill sm red">No next step</span>;
    case 'stale':
      return <span className="pill sm or">Overdue: {fDay(lead.step_due!)}</span>;
    case 'ok':
      return <span className="pill sm gr">{fShort(lead.step_due!)}</span>;
    default:
      return null;
  }
}

// Brand-like hues, darkened where needed so white 11px text passes WCAG AA.
const SRC: Record<string, [string, string]> = {
  'FB instant form': ['FB', '#1466D1'],
  'Cold email': ['Email', '#9A6200'],
  'Cold call': ['Call', '#2F6FE4'],
  'Calendly (web)': ['Web', '#11804F'],
  'Simon’s calendar': ['Call', '#2F6FE4'],
  Referral: ['Ref', '#6E4BF2'],
  WhatsApp: ['WA', '#0E7A4C'],
};

export function SrcBadge({ source }: { source: string | null }) {
  const [label, color] = SRC[source ?? ''] ?? ['Other', '#697284'];
  return (
    <span className="srcb" style={{ background: color }} title={source ?? ''}>
      {label}
    </span>
  );
}

export function LocalTime({ city, tz }: { city: string | null; tz: string }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  }).format(now);
  return (
    <span className="lt" title="Local time for the lead">
      <Icon name="clock" />
      {city} {time}
    </span>
  );
}

export function RouteBar({ stage }: { stage: Stage }) {
  const end = ({ lost: 'LOST', disq: 'DQ', nurture: 'NUR' } as Record<string, string>)[stage];
  if (end) {
    return (
      <div className="route">
        {ROUTE.slice(0, 4).map((r) => (
          <span key={r.code} style={{ display: 'contents' }}>
            <span className="code">{r.code}</span>
            <span className="ln" />
          </span>
        ))}
        <span className="cur lost">{end}</span>
      </div>
    );
  }
  const idx = ROUTE.findIndex((r) => (r.stages as readonly string[]).includes(stage));
  return (
    <div className="route">
      {ROUTE.map((r, i) => (
        <span key={r.code} style={{ display: 'contents' }}>
          {i === idx ? (
            <span className="cur">{r.code}</span>
          ) : (
            <span className={`code ${i < idx ? 'done' : ''}`}>{r.code}</span>
          )}
          {i < ROUTE.length - 1 && <span className={`ln ${i < idx ? 'done' : ''}`} />}
        </span>
      ))}
    </div>
  );
}

export function DueText({ due, done }: { due: string; done: boolean }) {
  if (done) return <span className="tm">Done</span>;
  if (new Date(due) < new Date()) return <span className="tm late">Overdue</span>;
  if (dayDiff(due) === 0) return <span className="tm">Today</span>;
  return <span className="tm">{fDay(due)}</span>;
}

// Toast ------------------------------------------------------------------------

type ToastFn = (message: string, undo?: () => void | Promise<void>) => void;
const ToastContext = createContext<ToastFn>(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    message: string;
    undo?: (() => void | Promise<void>) | undefined;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const toast = useCallback<ToastFn>((message, undo) => {
    clearTimeout(timer.current);
    setState({ message, undo });
    timer.current = setTimeout(() => setState(null), undo ? 6000 : 2800);
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className={`toast ${state ? 'on' : ''}`}
        role="status"
        aria-live="polite"
        style={{ pointerEvents: state?.undo ? 'auto' : 'none' }}
      >
        {state?.message}
        {state?.undo && (
          <button
            type="button"
            onClick={() => {
              const undo = state.undo;
              setState(null);
              void undo?.();
            }}
          >
            Undo
          </button>
        )}
      </div>
    </ToastContext.Provider>
  );
}

// Drawer -----------------------------------------------------------------------

export type DrawerState =
  | { type: 'lead'; id: string; edit?: 'complete' | 'change' | undefined }
  | {
      type: 'newLead';
      /** Started from an unlinked call: prefill from its attendee, link it after. */
      fromCall?:
        { id: string; person?: string | undefined; email?: string | undefined } | undefined;
    }
  | { type: 'search' }
  | { type: 'menu' }
  | { type: 'call'; id: string }
  | { type: 'newCall' }
  | { type: 'fix'; provider: string }
  | { type: 'task'; id?: string | undefined; clientId?: string | undefined }
  | { type: 'clients' }
  | {
      type: 'event';
      /** calendar_events.id of an existing event; absent for a new one. */
      id?: string | undefined;
      start?: string | undefined;
      leadId?: string | undefined;
    }
  | { type: 'invite' }
  | { type: 'notifications' }
  | { type: 'auto'; key: string }
  | { type: 'callImport' }
  | null;

type DrawerApi = { drawer: DrawerState; open: (d: DrawerState) => void; close: () => void };
const DrawerContext = createContext<DrawerApi>({ drawer: null, open: () => {}, close: () => {} });
export const useDrawer = () => useContext(DrawerContext);

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const lastFocus = useRef<Element | null>(null);

  const open = useCallback((d: DrawerState) => {
    if (!lastFocus.current) lastFocus.current = document.activeElement;
    setDrawer(d);
  }, []);
  const close = useCallback(() => {
    setDrawer(null);
    (lastFocus.current as HTMLElement | null)?.focus?.();
    lastFocus.current = null;
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        open({ type: 'search' });
      } else if (e.key === 'Escape' && drawer) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer, open, close]);

  useEffect(() => {
    document.body.style.overflow = drawer ? 'hidden' : '';
  }, [drawer]);

  const api = useMemo(() => ({ drawer, open, close }), [drawer, open, close]);
  return <DrawerContext.Provider value={api}>{children}</DrawerContext.Provider>;
}

/** The sliding panel. Children render its .dhead/.dbody/.dfoot. */
export function DrawerShell({
  children,
  labelledBy = 'dtitle',
}: {
  children: ReactNode;
  labelledBy?: string;
}) {
  const { drawer, close } = useDrawer();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!drawer) return;
    const t = setTimeout(() => ref.current?.querySelector<HTMLElement>('.x')?.focus(), 50);
    return () => clearTimeout(t);
  }, [drawer]);

  function trap(e: React.KeyboardEvent) {
    if (e.key !== 'Tab' || !ref.current) return;
    const f = [
      ...ref.current.querySelectorAll<HTMLElement>('button,a[href],input,select,textarea'),
    ].filter((x) => !(x as HTMLButtonElement).disabled && x.offsetParent !== null);
    if (!f.length) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      <div className={`scrim ${drawer ? 'on' : ''}`} onClick={close} />
      <aside
        ref={ref}
        className={`drawer ${drawer ? 'on' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onKeyDown={trap}
      >
        {drawer && children}
      </aside>
    </>
  );
}

export function CloseX() {
  const { close } = useDrawer();
  return (
    <button className="x" aria-label="Close" onClick={close} type="button">
      ×
    </button>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="errbox" role="alert">
      {error instanceof Error ? error.message : String(error)}
    </p>
  );
}
